import React from "react";
import { Wheat, Coffee, Cookie, Droplet, AlertTriangle, Award, FileCheck, Globe2, Leaf, Boxes, ShieldCheck } from "lucide-react";
import { Hero, Section, Sub, Callout, Quote, NumberedItem, Bold } from "../modules/_shared";

export default function SoftCommoditiesPage() {
  return (
    <div data-testid="view-soft" className="max-w-5xl">
      <Hero
        moduleNum="07"
        eyebrowLabel="Secteur"
        italic="Soft"
        normal="commodities"
        accent="& alimentaire"
        icon={Wheat}
        description="Sucre, café, cacao, blé, maïs, riz, huile de palme, coton, caoutchouc — cours intégral : grades, ICUMSA, varietals, méthodes de traitement, prix bourse, certifications, mycotoxines, restrictions à l'export et commissions."
      />

      <Section eyebrow="Introduction" title="Le secteur le plus volumineux du commerce mondial" index="01">
        <p>
          Les soft commodities (sucre, café, cacao, blé, riz, huile de palme, coton…) représentent les plus
          gros volumes physiques du commerce international. Marges en pourcentage <Bold>plus faibles</Bold>
          qu&apos;en métaux précieux, mais <Bold>volumes énormes</Bold> : un deal sucre 100 000 t à 600 $ = 60 M$,
          soit 450 k$ de commission à 0,75 %.
        </p>
        <p>
          Particularités : exposition climatique directe (El Niño / La Niña), risques mycotoxines,
          restrictions d&apos;exportation imposées par les pays producteurs sans préavis, certifications de
          durabilité de plus en plus exigées (Fairtrade, Rainforest Alliance, UTZ, RSPO, GMP+).
        </p>
      </Section>

      {/* ============= SUCRE ============= */}
      <Section eyebrow="Partie 01 — Sucre" title="Le sucre — ICUMSA, le code universel" index="02">
        <Sub title="Généralités et importance stratégique">
          <p>
            L&apos;une des commodités les plus échangées au monde. Production annuelle <Bold>&gt; 180 Mt</Bold>.
            Issu de deux sources : la <Bold>canne à sucre (~ 80 % de la production mondiale)</Bold> en zones
            tropicales et subtropicales, et la <Bold>betterave sucrière (~ 20 %)</Bold> en pays tempérés
            (UE, USA, Russie, Ukraine).
          </p>
          <p>
            Canne : <Bold>Brésil (premier producteur et exportateur mondial de loin)</Bold>, Inde, Thaïlande,
            Australie, Pakistan. Betterave : France, Allemagne, Pologne, Russie, USA.
          </p>
          <p>
            Géopolitiquement sensible : alimentation de base de milliards de personnes. Beaucoup de pays
            maintiennent stocks stratégiques, quotas d&apos;import ou subventions. L&apos;Inde impose régulièrement
            des restrictions à l&apos;export quand sa production interne est insuffisante, créant des chocs de prix
            mondiaux.
          </p>
          <Callout icon={Droplet} title="Dimension énergétique brésilienne">
            Au Brésil, une partie significative de la canne est transformée non en sucre mais en
            <Bold> éthanol carburant (bioéthanol)</Bold>. Quand le pétrole monte, les raffineries brésiliennes
            produisent plus d&apos;éthanol et moins de sucre → offre mondiale réduite → prix qui montent.
          </Callout>
        </Sub>

        <Sub title="Sucre brut de canne (Raw Cane Sugar)">
          <p>
            Sucre non raffiné, directement issu de la 1ère transformation. Couleur brun-doré à brun foncé
            (mélasse). C&apos;est la référence du contrat <Bold>ICE Sugar No. 11</Bold>.
          </p>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li>Polarisation (pureté en saccharose) : <Bold>min 96° pol</Bold> (standard No. 11 = exactement 96)</li>
            <li>Couleur ICUMSA : 800 à 2 500 (plus le chiffre est élevé, plus c&apos;est foncé)</li>
            <li>Humidité max : 0,5 %</li>
            <li>Matières insolubles : très faible</li>
          </ul>
          <p className="text-sm italic text-[#A1A1AA]">
            La polarisation, mesurée par polarimétrie optique, est la mesure clé. Un sucre à 98° pol vaut plus
            qu&apos;un sucre à 96° pol.
          </p>
        </Sub>

        <Sub title="ICUMSA — l'échelle de couleur universelle">
          <p>
            L&apos;<Bold>ICUMSA (International Commission for Uniform Methods of Sugar Analysis)</Bold> définit les
            méthodes de mesure et standards du sucre. Plus la valeur est basse, plus le sucre est blanc et
            raffiné.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="ICUMSA 45 — blanc raffiné HQ"
              purity="≥ 99,8° pol"
              body="Couleur blanc brillant. Le grade le plus demandé au commerce international. Alimentation, boissons, confiserie. Référence du contrat ICE Sugar No. 5."
            />
            <GradeCard
              name="ICUMSA 100 — blanc qualité standard"
              purity="≥ 99,5° pol"
              body="Légèrement moins blanc. Acceptable pour la plupart des usages alimentaires."
            />
            <GradeCard
              name="ICUMSA 150 — blanc standard"
              purity="≥ 99,5° pol"
              body="Couleur blanc-crème. Alimentation et certains usages industriels."
            />
            <GradeCard
              name="ICUMSA 600 – 1200 — VHP / VVHP"
              purity="99,3 – 99,5° pol"
              body="« Very High Pol ». Beige à brun clair. Intermédiaire raw / blanc raffiné. Très utilisé par les raffineries qui le transforment en blanc."
            />
            <GradeCard
              name="ICUMSA 1200 – 2500 — sucre brut standard"
              purity="96 – 99° pol"
              body="Brun-doré. Le sucre brut classique du contrat No. 11."
            />
            <GradeCard
              name="ICUMSA 3000+ — très brut / plantation"
              purity="brun foncé"
              body="Fermentation industrielle (éthanol, levures) et certaines applications spécifiques."
            />
          </div>
          <Sub title="Autres formes commerciales">
            <ul className="list-disc list-inside text-sm space-y-1">
              <li><Bold>Sucre roux (Brown / Turbinado / Demerara)</Bold> : partiellement raffiné, fine pellicule de mélasse. Marché consommateur, image « naturelle »</li>
              <li><Bold>Sucre de betterave blanc</Bold> : chimiquement identique au sucre de canne ICUMSA 45. Produit principalement en Europe</li>
              <li><Bold>Mélasse (molasses)</Bold> : sous-produit. 45-60 % de sucres. Éthanol, alimentation animale, rhum, levures</li>
            </ul>
          </Sub>
        </Sub>

        <Sub title="Prix et cotation">
          <p>
            Deux contrats de référence mondiaux : <Bold>ICE Sugar No. 11 (NY)</Bold> pour le sucre brut, prix
            en cents/lb (× 22,0462 pour USD/t) ; <Bold>ICE Sugar No. 5 (Londres)</Bold> pour le sucre blanc,
            prix en USD/t directement utilisable.
          </p>
          <Callout icon={TrendingUpIcon} title="« White Premium » / Refining margin">
            La différence entre No. 5 et No. 11 (converti en USD/t) représente la valeur ajoutée du raffinage.
            Elle varie de <Bold>50 à 200 $/t</Bold> selon les conditions de marché.
          </Callout>
          <h5 className="font-serif text-lg text-white mt-6">No. 11 — sucre brut (cts/lb)</h5>
          <PriceTable
            rows={[
              { y: "2020", v: "~ 13 cts/lb (~ 287 $/t)" },
              { y: "2021", v: "~ 18 cts/lb (~ 397 $/t)" },
              { y: "2022", v: "~ 19 cts/lb (~ 419 $/t)" },
              { y: "2023", v: "~ 24 cts/lb (~ 529 $/t — El Niño)" },
              { y: "2024", v: "~ 20 – 23 cts/lb (~ 440 – 507 $/t)" },
            ]}
            record="Record : 36 cts/lb (1980), récemment 28,5 cts (2023)"
          />
          <h5 className="font-serif text-lg text-white mt-6">No. 5 — sucre blanc (USD/t)</h5>
          <PriceTable
            rows={[
              { y: "2020", v: "~ 370 $/t" },
              { y: "2021", v: "~ 490 $/t" },
              { y: "2022", v: "~ 510 $/t" },
              { y: "2023", v: "~ 680 $/t (pic 750 $/t)" },
              { y: "2024", v: "~ 550 – 650 $/t" },
            ]}
          />
        </Sub>

        <Sub title="Formation du prix dans un deal">
          <p>
            <Bold>Prix commercial</Bold> = ICE No. 11 (ou No. 5) au jour de pricing + Prime/décote qualité +
            Prime origine + Fret + Assurance + Commission.
          </p>
          <h5 className="font-serif text-lg text-white mt-4">Primes qualité — sur No. 11</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li>Sucre 99° pol (au lieu de 96° pol standard) : prime 8 à 15 $/t</li>
            <li>ICUMSA 45 (blanc raffiné) : cotation directe sur le No. 5</li>
            <li>Sucre Fairtrade ou bio : prime 20 à 80 $/t selon acheteurs</li>
          </ul>
          <h5 className="font-serif text-lg text-white mt-4">Primes d&apos;origine indicatives (spot)</h5>
          <RegionalPrimes
            rows={[
              { region: "Brésil Centre-Sud (référence)", value: "0 à + 10 $/t" },
              { region: "Thaïlande (2ᵉ exportateur)", value: "+ 5 à 20 $/t" },
              { region: "Australie (qualité reconnue)", value: "+ 10 à 25 $/t" },
              { region: "Guatemala / Nicaragua", value: "+ 5 à 15 $/t" },
              { region: "Cuba", value: "souvent décote (logistique/politique)" },
            ]}
          />
          <h5 className="font-serif text-lg text-white mt-4">Facteurs influençant le prix</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>Production brésilienne</Bold> : le Brésil = ~ 50 % des exports mondiaux. Sécheresse Centre-Sud → -5 % à -15 % récolte → +20 % à +40 % sur le prix mondial</li>
            <li><Bold>Arbitrage éthanol/sucre au Brésil</Bold> : moulins peuvent choisir. Pétrole cher → plus d&apos;éthanol → moins de sucre → hausse</li>
            <li><Bold>Production indienne</Bold> : 1ᵉʳ producteur mondial mais politique d&apos;export imprévisible (restrictions soudaines en 2023)</li>
            <li><Bold>El Niño / La Niña</Bold> : sécheresses Inde, Thaïlande, Australie ; pluies excessives Brésil → réduction production mondiale</li>
            <li><Bold>Cours du dollar US</Bold> : USD fort pèse sur les prix, USD faible les stimule</li>
          </ul>
        </Sub>

        <Sub title="Processus d'achat du sucre — étapes complètes">
          <NumberedItem n="01" title="Définir précisément les spécifications">
            Type exact (brut No. 11, blanc ICUMSA 45, VHP…), polarisation min, ICUMSA max, humidité max,
            volume en t métriques, fréquence (mensuelle / trimestrielle / annuelle), origine acceptée, port
            destination + incoterm, paiement, certifications (Halal, Kosher, bio, Fairtrade), emballage (vrac
            ou sacs 50 kg ou big bags 1 t).
          </NumberedItem>
          <NumberedItem n="02" title="Sourcing du vendeur">
            Trading houses (Louis Dreyfus, Cargill, Bunge, Tereos, Sucden, Noble Group), raffineries nationales
            (Thaïlande, Brésil), coopératives, négociants locaux. Brésil : moulins (usinas) Centre-Sud — Raízen
            (JV Cosan/Shell), São Martinho, Biosev. Thaïlande : Thai Sugar Trading Corp, <Bold>Mitr Phol Sugar Corp</Bold>
            (le plus grand producteur thaï), Thai Roong Ruang Sugar Group.
          </NumberedItem>
          <NumberedItem n="03" title="Documentation spécifique">
            <ul className="list-disc list-inside text-sm space-y-1">
              <li><Bold>SGS / Intertek Pre-Shipment Inspection</Bold> : qualité (polarisation, ICUMSA, humidité), poids (pont-bascule certifié), conditionnement</li>
              <li><Bold>COA</Bold> (Certificate of Analysis) émis par labo raffinerie</li>
              <li><Bold>Fumigation Certificate</Bold> si exporté en sacs (anti-insectes)</li>
              <li><Bold>Halal</Bold> (organisme islamique accrédité — JAKIM, MUI, OU…) pour Moyen-Orient, Asie SE, Afrique du Nord</li>
              <li><Bold>Kosher</Bold> (rabbin ou organisme accrédité) pour acheteurs juifs / israéliens</li>
              <li><Bold>Phytosanitary Certificate</Bold> par autorités agricoles du pays exportateur</li>
            </ul>
          </NumberedItem>
          <NumberedItem n="04" title="Contrat et termes clés">
            Référence aux contrats types <Bold>FOSFA</Bold> ou conditions ICE. Préciser : base de pricing
            (No. 11 ou No. 5 + prime fixe ou variable), date de pricing (jour de chargement, moyenne période,
            prix fixe), tolérance quantité (± 5 % ou ± 10 % en vrac), clause qualité (analyse contradictoire,
            arbitrage FOSFA ou CCI), modalités de paiement.
          </NumberedItem>
          <NumberedItem n="05" title="Logistique">
            Vrac → bulk carriers ; sacs → conteneurs 20 pieds (~ 20-25 t/conteneur) ; big bags 1 t → conteneurs
            ou conventionnel. Conservation : 24-36 mois si humidité &lt; 65 %. Au-delà : durcissement,
            moisissures, perte de polarisation.
          </NumberedItem>
        </Sub>

        <Sub title="Incoterms préférentiels pour le sucre">
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>FOB</Bold> port brésilien ou thaï : standard pour gros volumes en vrac</li>
            <li><Bold>CIF</Bold> port destination : acheteurs moins expérimentés en logistique maritime</li>
            <li><Bold>CFR</Bold> : acheteur a ses propres contrats d&apos;assurance</li>
            <li><Bold>DAP</Bold> entrepôt acheteur : livraisons terrestres intra-continentales (raffinerie EU → usine agro-alimentaire EU)</li>
          </ul>
        </Sub>

        <Sub title="Commissions et marges sur le sucre">
          <p>Commission typique : <Bold>0,5 % à 1,5 %</Bold> sur valeur CIF totale.</p>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>50 000 t blanc ICUMSA 45 à 600 $/t CIF = 30 M$</Bold> → commission 1 % = <Bold>300 k$</Bold></li>
            <li>100 000 t brut à 450 $/t FOB = 45 M$ → commission 0,75 % = 337,5 k$</li>
          </ul>
        </Sub>
      </Section>

      {/* ============= CAFÉ ============= */}
      <Section eyebrow="Partie 02 — Café" title="Le café — Arabica vs Robusta" index="03">
        <Sub title="Généralités et importance stratégique">
          <p>
            <Bold>Deuxième commodité la plus échangée au monde après le pétrole</Bold> (en valeur). Cultivé
            dans &gt; 70 pays dans la « Coffee Belt » (25°N – 30°S). Plus de <Bold>2,5 milliards de tasses</Bold>
            consommées chaque jour. Marché : ~ 100 Md$ au détail, ~ 30-40 Md$ à l&apos;export pays producteurs.
          </p>
          <p>
            Spécificité unique : coexistence d&apos;un marché de masse standardisé (soluble industriel, capsules,
            supermarché) et d&apos;un marché de niche très premium (specialty, single origin, micro-lots) avec
            des prix variant <Bold>du simple au décuple</Bold>.
          </p>
        </Sub>

        <Sub title="Les deux espèces botaniques">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="Arabica (Coffea arabica)"
              purity="60 – 65 % production mondiale"
              body="Espèce noble, complexe aromatiquement. Cultivée en altitude (800-2 200 m), climat tempéré. Notes fruitées, florales, acidité caractéristique. Brésil, Colombie, Éthiopie, Honduras, Guatemala, Costa Rica, Kenya, Tanzanie, Pérou, Mexique, Inde. Base du contrat ICE C (NY)."
            />
            <GradeCard
              name="Robusta (Coffea canephora)"
              purity="35 – 40 % production mondiale"
              body="Plus robuste. Basse altitude (0-800 m), rendements élevés. Goût fort, amer, caféine ~ 2× arabica. Indispensable en blends espresso et café soluble (Nescafé). Vietnam, Brésil, Côte d&apos;Ivoire, Ouganda, Indonésie, Congo. Base du contrat ICE Robusta (Londres)."
            />
          </div>
        </Sub>

        <Sub title="Méthodes de traitement post-récolte">
          <p>La méthode utilisée après la récolte des cerises influence considérablement le goût et la valeur.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              name="Natural / Dry Process"
              purity="3 à 6 semaines de séchage"
              body="Cerises séchées entières au soleil (lits africains, terrasses béton). Le grain absorbe sucres et arômes de la pulpe. Notes fruitées, rondes, baies, vin, chocolat. Éthiopie, Brésil, Yémen."
              accent
            />
            <GradeCard
              name="Lavé / Fully Washed"
              purity="fermentation 24-72 h"
              body="Pulpe retirée mécaniquement. Grain parchemin fermenté en bassins puis lavé et séché. Café plus « propre », acidité marquée, arômes définis. Colombie, Kenya, Tanzanie, Amérique Centrale."
            />
            <GradeCard
              name="Honey / Miel (semi-washed)"
              purity="mucilage partiel"
              body="Pulpe retirée mais mucilage partiellement laissé. White / Yellow / Red / Black Honey selon quantité. Profils intermédiaires, douceur et complexité fruitée. Costa Rica, Bolivie, Brésil."
            />
            <GradeCard
              name="Wet-Hulled (Giling Basah)"
              purity="quasi exclusif Indonésie"
              body="Parchemin retiré alors que le grain est encore humide. Notes terreuses, tabac, herbes, cuir. Sumatra Mandheling = exemple le plus connu."
            />
          </div>
        </Sub>

        <Sub title="Classification du café vert (Green Coffee)">
          <h5 className="font-serif text-lg text-white">Brésil — multicritère</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>Par type (défauts)</Bold> : de Type 2 (le meilleur) à Type 8. Type 2 = quasi sans défauts (max défauts pour 300 g d&apos;échantillon)</li>
            <li><Bold>Par taille de grain</Bold> : calibre 17/18 = grains de plus grande taille = plus valorisés</li>
            <li><Bold>Par boisson</Bold> : « Strictly Soft » (le meilleur) → « Soft » → « Softish » → « Hardish » → « Hard »</li>
            <li><Bold>Par traitement</Bold> : Natural, Pulped Natural (honey), Washed</li>
          </ul>
          <p className="text-xs italic text-[#A1A1AA]">
            Exemple typique : <em>« Santos NY2/3, Type 2/3, Fine Cup, Screen 17/18, Natural »</em> = café de Santos
            (port d&apos;export), qualité entre NY 2 et 3, Type 2-3 au cupping, bon profil, calibre 17/18, naturel.
          </p>

          <h5 className="font-serif text-lg text-white mt-6">Colombie</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>Supreme</Bold> : calibre 18+ — la plus haute classification colombienne</li>
            <li><Bold>Excelso (EP — Excelso Premium)</Bold> : calibre 14-16. Standard courant</li>
            <li><Bold>UGQ (Usual Good Quality)</Bold> : qualité commerciale standard</li>
          </ul>
          <p className="text-xs italic text-[#A1A1AA]">
            Café colombien presque exclusivement lavé. La <Bold>Federación Nacional de Cafeteros (FNC)</Bold>
            contrôle et certifie une grande partie des exportations. Marque commerciale : <em>Juan Valdez</em>.
          </p>

          <h5 className="font-serif text-lg text-white mt-6">Éthiopie</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li>Grade 1 : quasi sans défauts, profil tasse excellent. Specialty premium</li>
            <li>Grade 2 : très peu de défauts. Haute qualité</li>
            <li>Grade 3 : quelques défauts. Bonne qualité commerciale</li>
            <li>Grade 4-5 : commercial standard à inférieur</li>
          </ul>
          <p className="text-xs italic text-[#A1A1AA]">
            Origines clés : <Bold>Yirgacheffe</Bold> (lavés et naturels extraordinaires, jasmin, bergamote),
            <Bold> Sidamo / Sidama</Bold> (notes fruitées), <Bold>Guji</Bold> (naturels premium),
            <Bold> Harrar</Bold> (vin / fruits rouges), Kaffa, Bench Maji, Limu, Jimma.
          </p>

          <h5 className="font-serif text-lg text-white mt-6">Kenya</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>AA</Bold> : les plus gros grains (calibre 18+). Premium de référence</li>
            <li><Bold>AB</Bold> : calibre 15-16. Très bonne qualité</li>
            <li><Bold>C</Bold> : grains plus petits, même qualité</li>
            <li><Bold>E (Elephant bean)</Bold> : grains doubles (Peaberry doubles), rare</li>
            <li>TT/T : grains légers ou défectueux</li>
            <li><Bold>PB (Peaberry)</Bold> : grains ronds (1 graine au lieu de 2). Goût souvent plus intense, recherché en specialty</li>
          </ul>

          <h5 className="font-serif text-lg text-white mt-6">SCA — Specialty Coffee Association</h5>
          <p className="text-sm">
            Protocole de notation sensorielle (cupping) sur 100 points : fragrance/arôme, saveur, acidité,
            corps, finish, équilibre, uniformité, absence de défauts.
          </p>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li>Score &gt; <Bold>80</Bold> : Specialty Coffee</li>
            <li>Score 85-89 : Excellent / Outstanding</li>
            <li>Score 90+ : Extraordinary / Perfect — prix exponentiellement plus élevés (Best of Panama, Cup of Excellence, Kenya Auction). 10 à 50 fois le prix du marché pour des micro-lots</li>
          </ul>
        </Sub>

        <Sub title="Prix et cotation">
          <h5 className="font-serif text-lg text-white">ICE Coffee « C » — Arabica (NY)</h5>
          <p className="text-sm">Cents US par livre (× 22,0462 pour USD/t).</p>
          <PriceTable
            rows={[
              { y: "2020", v: "~ 125 cts/lb (~ 2 756 $/t)" },
              { y: "2021", v: "~ 195 cts/lb (~ 4 298 $/t)" },
              { y: "2022", v: "~ 215 cts/lb (~ 4 739 $/t)" },
              { y: "2023", v: "~ 165 cts/lb (~ 3 638 $/t)" },
              { y: "2024", v: "~ 200 – 290 cts/lb (pénurie brésilienne)" },
            ]}
            record="Record : 339 cts/lb (1977), récemment 310 cts/lb (2024)"
          />
          <h5 className="font-serif text-lg text-white mt-6">ICE Robusta (Londres)</h5>
          <PriceTable
            rows={[
              { y: "2020", v: "~ 1 280 $/t" },
              { y: "2021", v: "~ 1 780 $/t" },
              { y: "2022", v: "~ 2 050 $/t" },
              { y: "2023", v: "~ 2 450 $/t" },
              { y: "2024", v: "~ 2 800 – 4 200 $/t (sécheresse Vietnam)" },
            ]}
            record="Record : > 4 000 $/t en 2024"
          />
          <p className="text-xs italic text-[#A1A1AA]">
            En 2024, le robusta a dépassé par moments <Bold>4 000 $/t</Bold>, en raison d&apos;une sécheresse sévère
            au Vietnam et en Indonésie, devenant presque aussi cher que certains arabicas commerciaux et
            bouleversant les habitudes de blending des torréfacteurs.
          </p>
        </Sub>

        <Sub title="Formation du prix dans un deal">
          <p>
            <Bold>Arabica</Bold> : Prix C contract (ICE NY) + Différentiel (prime ou décote selon origine et
            qualité). Le différentiel est <Bold>la clé</Bold> de la négociation.
          </p>
          <h5 className="font-serif text-lg text-white mt-4">Différentiels indicatifs</h5>
          <RegionalPrimes
            rows={[
              { region: "Colombie Excelso EP", value: "+ 15 à 30 cts/lb" },
              { region: "Éthiopie Yirgacheffe Grade 1 Washed", value: "+ 30 à 80 cts/lb" },
              { region: "Brésil Natural NY 2", value: "0 à + 10 cts/lb" },
              { region: "Robusta Vietnam Gr2", value: "London Robusta + diff." },
            ]}
          />
          <p className="text-sm italic text-[#A1A1AA]">
            Specialty (SCA 85+) : prix déconnectés du marché à terme. Multiple du C : C+100 à C+200 cts/lb
            pour 87-88. Micro-lot 90+ : 20, 30, 50 voire 100 $/lb aux enchères d&apos;exception.
          </p>
        </Sub>

        <Sub title="Documents et logistique du café">
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>Certificat ICO</Bold> (International Coffee Organization) : obligatoire dans la plupart des pays membres ICO. N° unique de référence</li>
            <li><Bold>Phytosanitary Certificate</Bold> : autorités agricoles nationales</li>
            <li><Bold>Rapport de cupping</Bold> par <Bold>Q Grader</Bold> certifié ou Authorized Sensory Panelist SCA</li>
            <li>Certifications durabilité : Rainforest Alliance, UTZ (fusionnée), Fairtrade, Bio, Bird Friendly</li>
            <li>Certificat d&apos;analyse résidus pesticides (exigé UE — limites MRL)</li>
          </ul>
          <h5 className="font-serif text-lg text-white mt-4">Logistique</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li>Conteneurs réfrigérés (reefers, 15 °C, humidité contrôlée) pour la qualité ; standard pour commercial</li>
            <li>Conservation 12-24 mois sans perte significative ; au-delà : <em>aged coffee</em></li>
            <li>Sacs jute <Bold>60 kg</Bold> (Brésil, Amérique Centrale, Colombie). Éthiopie : 60 kg ou grain bags 50 kg en specialty</li>
            <li>Conteneur 20&apos; standard ≈ <Bold>275 sacs de 60 kg = 16,5 t</Bold></li>
          </ul>
        </Sub>

        <Sub title="Acteurs et commissions">
          <p>
            Trading houses dominantes : <Bold>Neumann Kaffee Gruppe (NKG, Allemagne — le plus gros indépendant)</Bold>,
            Volcafe (filiale ED&amp;F Man), Ecom Agroindustrial Corp, Sucafina (CH), Louis Dreyfus Company (division
            café), Mercon Coffee Group. Ils contrôlent <Bold>40 % à 60 %</Bold> du commerce mondial de café vert.
          </p>
          <p>
            Comme intermédiaire indépendant, vous vous positionnez entre les producteurs/exportateurs locaux
            (coopératives, mills, exportateurs nationaux) et les importateurs régionaux, torréfacteurs
            industriels, ou acheteurs de spécialité.
          </p>
          <h5 className="font-serif text-lg text-white mt-4">Commissions</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li>Café commercial : <Bold>1 % à 2 %</Bold> sur valeur CIF</li>
            <li>Exemple : 20 conteneurs Arabica brésilien (330 t) à 3 000 $/t CIF = 990 k$ → 1,5 % = <Bold>14 850 $</Bold></li>
            <li>Café de spécialité premium : 2 % à 5 % (volumes plus faibles, qualification intensive)</li>
          </ul>
        </Sub>
      </Section>

      {/* ============= CACAO ============= */}
      <Section eyebrow="Partie 03 — Cacao" title="Le cacao — La crise historique 2024" index="04">
        <Sub title="Généralités">
          <p>
            Cultivé exclusivement zone tropicale (20°N – 20°S). Matière première du chocolat (industrie
            agroalimentaire &gt; <Bold>100 Md$</Bold> au détail / an). <Bold>Côte d&apos;Ivoire + Ghana = ~ 60-65 % de
            la production mondiale</Bold> → vulnérabilités géopolitiques majeures.
          </p>
          <Callout icon={AlertTriangle} title="Crise historique 2024">
            En 2024, une grave crise de production en Côte d&apos;Ivoire et au Ghana (maladie des cabosses
            noires + climat défavorable) a propulsé les prix à des records absolus, dépassant
            <Bold> 10 000 $/t</Bold> — du jamais vu. Filière au cœur de débats éthiques (travail des
            enfants, déforestation). Initiatives : Cocoa &amp; Forests Initiative (CFI), <Bold>EUDR</Bold> (Règlement
            UE déforestation 2024).
          </Callout>
        </Sub>

        <Sub title="Variétés botaniques">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="Forastero"
              purity="80 – 85 % production mondiale"
              body="Variété la plus robuste, rendements élevés. Profil chocolat « classique », fort, légèrement amer. Côte d&apos;Ivoire, Ghana, Brésil (Bahia)."
            />
            <GradeCard
              name="Criollo"
              purity="< 5 % production mondiale"
              body="Variété originelle Amérique Centrale / Andes. Très fragile, rendements faibles. Profil exceptionnel : noix, fruits rouges, tabac. Mexique, Vénézuela, Guatemala, Nicaragua."
            />
            <GradeCard
              name="Trinitario"
              purity="10 – 15 % production"
              body="Hybride Forastero × Criollo (Trinidad XVIIIᵉ siècle). Bon équilibre robustesse / complexité. Trinité-et-Tobago, Vénézuela, Équateur, Papouasie-NG."
            />
            <GradeCard
              name="Nacional / Arriba (Équateur)"
              purity="cacao fin endémique"
              body="Notes florales jasmin. CCN-51 (commercial) / Nacional (variété fine). L&apos;un des cacaos fins les plus réputés au monde."
            />
          </div>
        </Sub>

        <Sub title="Classification commerciale internationale">
          <h5 className="font-serif text-lg text-white">Fine or Flavour Cocoa (Cacao Fin / d&apos;Arôme)</h5>
          <p className="text-sm">
            L&apos;<Bold>ICCO</Bold> maintient une liste : Équateur (jusqu&apos;à 75 % de sa production), Trinité-et-Tobago,
            Vénézuela, São Tomé et Príncipe, Java, Papouasie-NG, Jamaïque… Soit ~ 8 % de la production mondiale
            mais <Bold>prix premium significatifs</Bold>.
          </p>
          <h5 className="font-serif text-lg text-white mt-4">Bulk Cocoa (Cacao de Masse)</h5>
          <p className="text-sm">
            Côte d&apos;Ivoire, Ghana, Cameroun, Nigeria, Brésil (Bahia). Cacao industriel de référence, base des
            grandes marques de chocolat.
          </p>
          <h5 className="font-serif text-lg text-white mt-4">Grades nationaux (CdI / Ghana)</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>Grade 1 (GH1 Ghana / Grade 1 CdI)</Bold> : meilleur grade. Fèves sèches, bien fermentées, défauts &lt; 5 %, humidité ≤ 7,5 %. Livrable sur le contrat ICE</li>
            <li><Bold>Grade 2</Bold> : quelques défauts supplémentaires, encore acceptable industriel. Décote vs Grade 1</li>
            <li><Bold>Main Crop vs Mid-Crop</Bold> : Main = oct-mars (70-80 % production annuelle, meilleure qualité) ; Mid = mai-août (20-30 %)</li>
          </ul>
          <Callout icon={Leaf} title="L'importance de la fermentation">
            Étape post-récolte la plus critique. Fèves fermentées en tas (sous feuilles de bananier) ou
            caisses pendant <Bold>3 à 8 jours</Bold>. Sucres → alcool → acide acétique : développe les
            précurseurs d&apos;arômes du chocolat. Sous-fermentation : pas d&apos;arômes développés.
            Sur-fermentation : trop acide, défauts.
          </Callout>
        </Sub>

        <Sub title="Prix et cotation">
          <p>
            <Bold>ICE Cacao (NY)</Bold> contrat « Ivory Coast/Ghana », USD/t. <Bold>ICE Cacao (Londres,
            ex-LIFFE)</Bold> en GBP/t. Corrélation très forte mais arbitrages possibles.
          </p>
          <PriceTable
            rows={[
              { y: "2020", v: "~ 2 400 $/t" },
              { y: "2021", v: "~ 2 450 $/t" },
              { y: "2022", v: "~ 2 600 $/t" },
              { y: "2023", v: "~ 3 400 $/t" },
              { y: "Avril 2024", v: "~ 10 800 $/t (record absolu)" },
              { y: "Après 2024", v: "~ 6 000 – 8 000 $/t" },
            ]}
            record="Record absolu : ~ 10 800 $/t · avril 2024"
          />
          <p className="text-xs italic text-[#A1A1AA]">
            La flambée de 2024 est la plus spectaculaire de l&apos;histoire du cacao et illustre la vulnérabilité
            aux chocs de production en Afrique de l&apos;Ouest.
          </p>
          <h5 className="font-serif text-lg text-white mt-4">Différentiels indicatifs (avant crise 2024)</h5>
          <RegionalPrimes
            rows={[
              { region: "Côte d'Ivoire Grade 1 Main Crop", value: "+ 50 à 150 $/t" },
              { region: "Ghana Grade 1 (GH1) — réputé supérieur", value: "+ 100 à 200 $/t" },
              { region: "Cameroun Grade 1", value: "0 à + 50 $/t" },
              { region: "Équateur Arriba (cacao fin)", value: "+ 300 à 600 $/t" },
              { region: "Vénézuela Criollo", value: "+ 500 à 2 000 $/t (niche)" },
            ]}
          />
        </Sub>

        <Sub title="Documents spécifiques">
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>Certificat de qualité COCOBOD</Bold> (Ghana) : tout lot inspecté et certifié par le Ghana Cocoa Board</li>
            <li><Bold>Certificat CCC</Bold> (Côte d&apos;Ivoire) : Conseil Café-Cacao supervise les exports. Exportateurs agréés</li>
            <li><Bold>Certificat EUDR</Bold> (depuis 2024) : géolocalisation GPS de chaque parcelle pour prouver absence de déforestation après 31 décembre 2020</li>
            <li>Certifications durabilité : Rainforest Alliance, UTZ, Fairtrade, bio. Engagements publics chez Mars, Nestlé, Mondelez, Ferrero</li>
          </ul>
        </Sub>
      </Section>

      {/* ============= BLÉ ============= */}
      <Section eyebrow="Partie 04 — Blé" title="Le blé — Le pain du monde" index="05">
        <Sub title="Généralités">
          <p>
            Céréale la plus cultivée en surface au monde. Base de l&apos;alimentation eurasiatique. Géopolitiquement
            explosif : la dépendance MENA aux imports ukrainien et russe a été dramatiquement illustrée par la
            guerre en Ukraine depuis 2022. Production mondiale &gt; <Bold>760 Mt/an</Bold>.
          </p>
          <p>
            Principaux exportateurs : <Bold>Russie (1ᵉʳ exportateur mondial)</Bold>, UE, USA, Canada, Australie,
            Argentine, Ukraine.
          </p>
        </Sub>

        <Sub title="Les types et grades du blé">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard name="Hard Red Winter (HRW)" purity="11 – 13 % protéines" body="USA Kansas, Oklahoma, Nebraska. Variété de référence du contrat CBOT. Pain blanc, pizza." />
            <GradeCard name="Soft Red Winter (SRW)" purity="9 – 11 % protéines" body="Est USA. Biscuits, gâteaux, craquins, pâtisserie." />
            <GradeCard accent name="Hard Red Spring (HRS)" purity="13 – 16 % protéines" body="Nord USA (Minnesota, Dakotas) + Canada. Le blé à haute protéine par excellence. Pain artisanal, pâtes haut de gamme. Canadian HRS particulièrement réputé." />
            <GradeCard name="Durum (blé dur)" purity="12 – 14 % protéines" body="Structure gluten spécifique. Semoule et pâtes alimentaires. USA Dakota du Nord, Canada Saskatchewan, Italie, France (sud), Australie, Moyen-Orient. Coté séparément." />
            <GradeCard name="Soft White Wheat" purity="9 – 11 % protéines" body="Côte ouest USA (Washington, Oregon). Noodles asiatiques, pâtisserie." />
            <GradeCard name="Blé Français (MATIF)" purity="PS min 76 kg/hl, humidité max 15 %" body="Standard Euronext Paris : poids spécifique 76 kg/hl, protéines variables, humidité max 15 %." />
            <GradeCard name="Russian Milling Wheat 12,5 %" purity="12,5 % protéines" body="Standard export Russie. Pays MENA principaux acheteurs." />
          </div>
        </Sub>

        <Sub title="Prix et cotation">
          <p>
            <Bold>CBOT (Chicago)</Bold> : référence US, en cents US/boisseau. 1 boisseau de blé = 27,2155 kg.
            Conversion : (cts/bu / 27,2155) × 10 = USD/t. <Bold>MATIF (Euronext Paris)</Bold> : référence
            européenne en EUR/t.
          </p>
          <PriceTable
            rows={[
              { y: "2020", v: "CBOT ~ 540 cts/bu (~ 198 $/t) · MATIF ~ 200 €/t" },
              { y: "2021", v: "CBOT ~ 760 cts/bu (~ 279 $/t) · MATIF ~ 275 €/t" },
              { y: "2022", v: "CBOT ~ 920 cts/bu (~ 337 $/t) · pic 1 300 cts (Ukraine) · MATIF jusqu'à 420 €/t" },
              { y: "2023", v: "CBOT ~ 610 cts/bu (~ 224 $/t) · MATIF ~ 240 €/t" },
              { y: "2024", v: "CBOT ~ 540-590 cts/bu · MATIF ~ 200-220 €/t" },
            ]}
            record="Record 2022 : panique invasion russe (Russie + Ukraine = ~ 30 % des exports mondiaux)"
          />
        </Sub>

        <Sub title="Standards de qualité dans un deal">
          <ul className="list-disc list-inside text-sm space-y-2">
            <li><Bold>Poids spécifique (PS)</Bold> : min 76 kg/hl (jusqu&apos;à 82 pour qualités supérieures). Indicateur de densité — plus c&apos;est élevé, plus la teneur en farine est importante</li>
            <li><Bold>Teneur en protéines</Bold> : 12,5 % standard meunier ; 14 %+ pour blé de force. Détermine la force boulangère</li>
            <li><Bold>Humidité</Bold> : max 14-15 %. Au-dessus : moisissures et mycotoxines</li>
            <li><Bold>Impuretés</Bold> : grains brisés, corps étrangers, autres céréales</li>
            <li><Bold>Falling Number (Hagberg)</Bold> : mesure activité enzymatique. &lt; 220 secondes = pré-germination → dégrade la farine. Standard min 220, premium 250+</li>
            <li><Bold>Mycotoxines</Bold> : DON (Déoxynivalénol) max <Bold>1,25 ppm farine, 1,75 ppm blé brut</Bold> (UE)</li>
          </ul>
        </Sub>
      </Section>

      {/* ============= MAÏS ============= */}
      <Section eyebrow="Partie 05 — Maïs" title="Le maïs — La céréale la plus produite" index="06">
        <Sub title="Généralités">
          <p>
            Céréale la plus produite en volume au monde (~ <Bold>1,2 Md t/an</Bold>), devant blé et riz. Usages
            multiples : alimentation animale (~ 70 %), éthanol (USA), alimentation humaine (tortillas, polenta,
            ugali), industrie (amidon, sirop de glucose, plastiques biodégradables).
          </p>
          <p>
            <Bold>USA = de loin le 1ᵉʳ producteur et exportateur mondial</Bold>. Le « Corn Belt » (Iowa,
            Illinois, Indiana, Nebraska, Minnesota) est l&apos;une des zones agricoles les plus productives de la
            planète.
          </p>
        </Sub>
        <Sub title="Classification du maïs">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="Yellow Corn No. 2 (US Standard)"
              purity="humidité max 14 %"
              body="Référence mondiale, base CBOT. Corps étrangers max 3 %, grains endommagés max 5 %, endommagés chaleur max 0,2 %, aflatoxines max 20 ppb."
            />
            <GradeCard
              name="White Corn"
              purity="alimentation humaine directe"
              body="Tortillas, semoule, bouillie. Préféré en Afrique, Amérique Centrale, Mexique. Afrique du Sud, Argentine, USA exportateurs importants."
            />
            <GradeCard
              name="Maïs haute teneur en amidon"
              purity="industrie amidon"
              body="Variétés sélectionnées pour l'industrie de l'amidon."
            />
            <GradeCard
              name="OGM vs Non-OGM"
              purity="distinction commercialement importante"
              body="Maïs US > 90 % OGM. Pour Non-OGM : Ukraine, France (partiel), Brésil zones non-OGM."
            />
          </div>
        </Sub>
        <Sub title="Prix">
          <p>1 boisseau de maïs = 25,4012 kg.</p>
          <PriceTable
            rows={[
              { y: "2020", v: "~ 410 cts/bu (~ 161 $/t)" },
              { y: "2021", v: "~ 590 cts/bu (~ 232 $/t)" },
              { y: "2022", v: "~ 675 cts/bu (~ 265 $/t)" },
              { y: "2023", v: "~ 490 cts/bu (~ 193 $/t)" },
              { y: "2024", v: "~ 420-480 cts/bu (~ 165-189 $/t)" },
            ]}
          />
        </Sub>
      </Section>

      {/* ============= RIZ ============= */}
      <Section eyebrow="Partie 06 — Riz" title="Le riz — Pas de bourse mondiale unique" index="07">
        <Sub title="Généralités">
          <p>
            Aliment de base de <Bold>&gt; 3 milliards de personnes</Bold>, principalement en Asie (~ 80 % de la
            consommation mondiale). Contrairement au blé/maïs, le riz <Bold>n&apos;a pas de contrat à terme liquide
            de référence mondiale</Bold> — prix déterminés en gré à gré et indices FAO. Manque de transparence
            + fragmentation des marchés = <Bold>opportunités significatives pour l&apos;intermédiaire</Bold>.
          </p>
        </Sub>
        <Sub title="Classification — Par degré de transformation">
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>Paddy (Rough Rice)</Bold> : non décortiqué, avec balle. Stockage / transport court terme. Valeur faible</li>
            <li><Bold>Riz Cargo (Brown Rice)</Bold> : décortiqué, son conservé. Riche en nutriments mais conservation limitée</li>
            <li><Bold>Riz Blanc (White / Milled Rice)</Bold> : décortiqué + blanchi. Forme standard du commerce international. Conservation 12-18 mois</li>
            <li><Bold>Riz Étuvé (Parboiled)</Bold> : traité vapeur sous pression avant décorticage. Nutriments du son migrent dans le grain. Moins collant. Très populaire Afrique de l&apos;Ouest, Inde du Sud, USA</li>
            <li><Bold>Riz Brisé (Broken)</Bold> : fragments. Farines, noodles, alimentation animale ; consommation humaine en Afrique de l&apos;Ouest (Sénégal, Guinée). 25 % brisure le plus commun en Afrique</li>
          </ul>
        </Sub>
        <Sub title="Par type de grain">
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>Long Grain</Bold> : ratio L/l &gt; 3. Sec et aéré à la cuisson. Standard exports thaï, vietnamien, US</li>
            <li><Bold>Medium Grain</Bold> : ratio 2-3. Légèrement collant. Sushi (Japonica californien), risotto (Arborio, Carnaroli)</li>
            <li><Bold>Short Grain</Bold> : ratio &lt; 2. Très collant. Riz japonais, coréen, gluant asiatique</li>
          </ul>
        </Sub>
        <Sub title="Variétés premium et grades">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard accent name="Thai Hom Mali (Jasmine)" purity="Premium absolu" body="Variété parfumée de Thaïlande. Notes jasmin et pandan. Certification d&apos;origine obligatoire. + 20 % à + 50 % vs long grain standard." />
            <GradeCard name="Basmati 1121 Steam" purity="long grain — protégé" body="Inde / Pakistan. Géographiquement protégé. Halal, casher. Moyen-Orient. Inde 80 % exports mondiaux, Pakistan 20 %." />
            <GradeCard name="Vietnamese 5% Broken" purity="standard export" body="Long grain blanc 5 % brisure. Africa, Asie." />
            <GradeCard name="Camolino (Égypte)" purity="medium grain" body="Très populaire Moyen-Orient. Restrictions à l&apos;export ont réduit les volumes." />
            <GradeCard name="Gloria (Brésil)" purity="long grain" body="Compétitif sur les marchés africains." />
            <GradeCard name="IRRI / Pakistan 25%" purity="bas de gamme" body="25 % brisure. Africa, alimentation populaire." />
          </div>
          <h5 className="font-serif text-lg text-white mt-4">Grades commerciaux</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li>5 % Broken : grade premium</li>
            <li>15 % Broken : standard</li>
            <li>25 % Broken : le plus courant en Afrique de l&apos;Ouest</li>
            <li>100 % Broken : transformation et marchés spécifiques</li>
          </ul>
        </Sub>
        <Sub title="Prix et restrictions">
          <p>Pas de contrat à terme international liquide. Références : <Bold>FAO Rice Price Index</Bold>, <Bold>Thai White Rice 5 % FOB Bangkok</Bold>.</p>
          <PriceTable
            rows={[
              { y: "2024 — Thai 5% FOB Bangkok", v: "500 – 650 $/t" },
              { y: "2024 — Thai Hom Mali 5%", v: "800 – 1 100 $/t" },
              { y: "2024 — Vietnamese White 5%", v: "480 – 600 $/t" },
              { y: "2024 — Indian Parboiled 5%", v: "380 – 470 $/t" },
              { y: "2024 — Pakistan Basmati Super Kernel", v: "900 – 1 400 $/t" },
            ]}
          />
          <Callout icon={AlertTriangle} title="Restrictions à l'export — risque permanent">
            En 2023, l&apos;Inde a interdit l&apos;export de riz blanc non basmati et imposé une taxe 20 % sur le
            parboiled → <Bold>+ 20 % à + 30 % sur les prix mondiaux</Bold>. Surveillez les annonces
            gouvernementales.
          </Callout>
        </Sub>
      </Section>

      {/* ============= HUILE DE PALME ============= */}
      <Section eyebrow="Partie 07 — Palm Oil" title="Huile de palme — Indonésie + Malaisie 83 %" index="08">
        <Sub title="Généralités">
          <p>
            Huile végétale la plus produite au monde (~ <Bold>75 Mt/an</Bold>) et la moins chère. Présente dans
            ~ <Bold>50 % des produits alimentaires emballés</Bold>. Aussi : biodiesel, savons, cosmétiques,
            lubrifiants industriels.
          </p>
          <p>
            <Bold>Indonésie 58 %, Malaisie 25 %</Bold> de la production mondiale. Controverse environnementale
            permanente : déforestation Bornéo / Sumatra, menace orang-outan. Certification <Bold>RSPO</Bold>
            (Roundtable on Sustainable Palm Oil).
          </p>
        </Sub>
        <Sub title="Types et grades">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="CPO (Crude Palm Oil)"
              purity="FFA max 5 %, humidité max 0,25 %"
              body="Forme d&apos;export principale. Couleur orange-rouge (carotènes). Solide à T°C ambiante (point de fusion ~ 35 °C). Référence du contrat à terme Bursa Malaysia (BMD) et ICE."
            />
            <GradeCard
              name="RBDPO"
              purity="raffiné, décoloré, désodorisé"
              body="Couleur jaune pâle à beige. Margarines, shortenings, fritures industrielles, biscuits. Base oléochimiques."
            />
            <GradeCard
              name="Palm Olein"
              purity="fraction liquide"
              body="Issue du fractionnement RBDPO. Point fusion 15-20 °C. Principale huile de friture en Asie (frites, chips). Inde, Chine, Afrique."
            />
            <GradeCard
              name="Palm Stearin"
              purity="fraction solide"
              body="Issue du fractionnement RBDPO. Margarines solides, graisses végétales, confiserie, savons."
            />
            <GradeCard
              name="PKO (Palm Kernel Oil)"
              purity="huile de palmiste"
              body="Extraite du noyau du fruit (différent de la pulpe). Composition similaire à l&apos;huile de coco. Cosmétique et oléochimique."
            />
          </div>
        </Sub>
        <Sub title="Prix">
          <p>
            <Bold>Bursa Malaysia (BMD)</Bold> : MYR/t — référence mondiale. <Bold>ICE Futures Singapore</Bold> :
            USD/t alternatif.
          </p>
          <PriceTable
            rows={[
              { y: "2020", v: "~ 700 $/t" },
              { y: "2021", v: "~ 1 100 $/t" },
              { y: "2022", v: "~ 1 350 $/t · pic 1 800 $ (Ukraine, tournesol en pénurie)" },
              { y: "2023", v: "~ 870 $/t" },
              { y: "2024", v: "~ 900 – 1 050 $/t" },
            ]}
          />
        </Sub>
      </Section>

      {/* ============= COTON ============= */}
      <Section eyebrow="Partie 08 — Coton" title="Le coton — 26 Mt/an" index="09">
        <Sub title="Généralités">
          <p>
            Première fibre naturelle mondiale. Production &gt; <Bold>26 Mt/an</Bold>. Cultivé dans &gt; 80 pays.
            Fibres = 95 % des usages. Graines = huile (cottonseed oil) + tourteau alimentation animale.
          </p>
          <p>
            Producteurs : Chine 25 %, Inde 24 %, USA 13 %, Brésil 10 %, Pakistan 5 %. Exportateurs : USA (1ᵉʳ
            mondial), Brésil, Australie, Inde (variable).
          </p>
        </Sub>
        <Sub title="Classification">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="Upland (Gossypium hirsutum)"
              purity="~ 90 % production mondiale"
              body="Longueur fibre 1 à 13/32 pouces. Type Upland = grande majorité du coton commercialisé."
            />
            <GradeCard
              name="ELS / Pima / Égyptien"
              purity="fibre > 1 3/8 pouces"
              body="Plus fin, plus doux, plus résistant. Textiles premium (draps de luxe, chemises HQ). Égypte (Giza 86, Giza 87), Pérou, USA Pima, Inde Shankar-6 ELS. Prime + 20 % à + 60 %."
            />
          </div>
          <h5 className="font-serif text-lg text-white mt-4">Classifications USDA + HVI</h5>
          <p className="text-sm">
            USDA prend en compte : grade (couleur, préparation, contaminants), longueur (staple), résistance
            (strength), finesse (Micronaire), longueur d&apos;uniformité, débris. Réalisée par les classing offices
            USDA/AMS.
          </p>
          <p className="text-sm">
            HVI (High Volume Instrumentation) : méthode moderne par instruments automatisés. Mesure :
            longueur moyenne (Mean Length), uniformité (Uniformity Index), résistance (g/tex), finesse
            (Micronaire), réflectance (Rd) et jaune (+b).
          </p>
        </Sub>
        <Sub title="Prix">
          <p>
            <Bold>ICE Cotton No. 2 (NY)</Bold> : référence mondiale. Cents US/lb. Conversion : × 22,0462 pour
            USD/t.
          </p>
          <PriceTable
            rows={[
              { y: "2020", v: "~ 65 cts/lb (~ 1 432 $/t)" },
              { y: "2021", v: "~ 90 cts/lb (~ 1 984 $/t)" },
              { y: "2022", v: "~ 110 cts/lb (~ 2 425 $/t) · pic 155 cts" },
              { y: "2023", v: "~ 85 cts/lb (~ 1 874 $/t)" },
              { y: "2024", v: "~ 75-85 cts/lb" },
            ]}
          />
        </Sub>
      </Section>

      {/* ============= CAOUTCHOUC ============= */}
      <Section eyebrow="Partie 09 — Rubber" title="Le caoutchouc naturel — Hevea" index="10">
        <Sub title="Généralités">
          <p>
            Latex extrait de l&apos;<em>Hevea brasiliensis</em>, originaire d&apos;Amazonie, aujourd&apos;hui cultivé surtout
            en Asie SE. Irremplaçable pour pneus de gros porteurs et avions, gants médicaux, préservatifs —
            propriétés élastiques et mécaniques supérieures au synthétique (issu du pétrole).
          </p>
          <ProducersBar
            label="Producteurs"
            items={[
              { country: "Thaïlande", pct: "35 %" },
              { country: "Indonésie", pct: "28 %" },
              { country: "Vietnam", pct: "10 %" },
              { country: "Inde", pct: "7 %" },
              { country: "Malaisie", pct: "6 %" },
            ]}
          />
        </Sub>
        <Sub title="Grades">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="RSS (Ribbed Smoked Sheet)"
              purity="grades RSS 1 à 5"
              body="Le plus commun en commerce international. RSS 3 = grade de référence du contrat SICOM (Singapore Commodity Exchange)."
            />
            <GradeCard
              name="TSR (Technically Specified Rubber)"
              purity="TSR 10 / TSR 20"
              body="Specs précises : impuretés, cendres, azote, volatiles, plasticité, indice dégradation. TSR 20 = le plus courant. Thaïlande STR 20, Indonésie SIR 20, Vietnam SVR 20."
            />
            <GradeCard
              name="SVR (Standard Vietnamese Rubber)"
              purity="SVR CV"
              body="Caoutchouc vietnamien standardisé. Constant Viscosity très utilisé en production de gants."
            />
            <GradeCard
              name="Latex concentré"
              purity="62 % de teneur en caoutchouc"
              body="Latex liquide. Fabrication directe de gants, préservatifs, mousses."
            />
          </div>
        </Sub>
        <Sub title="Prix">
          <p>SICOM (Singapour) et TOCOM (Tokyo) — bourses de référence. USD/kg ou JPY/kg.</p>
          <PriceTable
            rows={[
              { y: "2024 indicatif", v: "~ 1,50 à 2,00 $/kg (~ 1 500 à 2 000 $/t)" },
            ]}
          />
        </Sub>
      </Section>

      {/* ============= TABLEAU RÉCAP ============= */}
      <Section eyebrow="Synthèse" title="Tableau récapitulatif des soft commodities" index="11">
        <div className="border border-white/10 overflow-hidden mt-4">
          <table className="w-full text-sm">
            <thead className="bg-[#141414] text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
              <tr>
                <th className="text-left p-3">Produit</th>
                <th className="text-left p-3">Bourse</th>
                <th className="text-left p-3">Grade std</th>
                <th className="text-left p-3">Prix 2024</th>
                <th className="text-left p-3">Comm.</th>
                <th className="text-left p-3">Incoterm</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {RECAP.map((r) => (
                <tr key={r.p}>
                  <td className="p-3 font-serif text-white">{r.p}</td>
                  <td className="p-3 text-[#A1A1AA]">{r.b}</td>
                  <td className="p-3 text-[#A1A1AA]">{r.g}</td>
                  <td className="p-3 font-mono text-[#D4AF37]">{r.pr}</td>
                  <td className="p-3 font-mono text-[#D4AF37]">{r.c}</td>
                  <td className="p-3 text-[#A1A1AA]">{r.i}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ============= TRANSVERSAL ============= */}
      <Section eyebrow="Aspects transversaux" title="Certifications, certifications qualité, logistique, paiement, risques" index="12">
        <Sub title="Certifications de durabilité — incontournables">
          <ul className="list-disc list-inside text-sm space-y-2">
            <li><Bold>Rainforest Alliance / UTZ</Bold> (fusionnés 2018) : durabilité environnementale et sociale. Très répandu café (Nespresso, Jacobs, Douwe Egberts), cacao, thé. Prime <Bold>50 à 200 $/t</Bold></li>
            <li><Bold>Fairtrade</Bold> : prix minimum garanti aux producteurs (floor price) + prime communautaire. Café (prix min 1,80 $/lb + prime 0,20 $/lb), cacao, sucre, coton</li>
            <li><Bold>Agriculture Biologique (AB / Organic)</Bold> : sans pesticides synthèse ni OGM. USDA Organic (USA), AB/Euro Feuille (UE), JAS (Japon). Conversion 3 ans. Prime <Bold>20 % à 100 %</Bold></li>
            <li><Bold>RSPO</Bold> : huile de palme sans déforestation, droits travailleurs / communautés. Exigée par grandes marques EU/US</li>
            <li><Bold>4C</Bold> : Common Code for the Coffee Community. Code de conduite minimum café durable. Première étape pour producteurs s&apos;engageant en durabilité</li>
          </ul>
        </Sub>

        <Sub title="Organismes de certification qualité">
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>SGS</Bold> (Société Générale de Surveillance) — leader mondial inspection / vérification / tests / certification. Bureau dans pratiquement tous les pays producteurs</li>
            <li><Bold>Bureau Veritas</Bold> — 2ᵉ acteur mondial</li>
            <li><Bold>Intertek</Bold> — 3ᵉ acteur mondial</li>
            <li><Bold>FOSFA</Bold> (Federation of Oils, Seeds and Fats Associations) — arbitrage huiles, graisses, farines</li>
            <li><Bold>GAFTA</Bold> (Grain and Feed Trade Association) — équivalent FOSFA pour céréales et aliments du bétail. Contrats GAFTA 100 (céréales en vrac) utilisés dans la majorité des transactions</li>
          </ul>
        </Sub>

        <Sub title="Logistique spécifique">
          <h5 className="font-serif text-lg text-white">Vrac sec (Dry Bulk)</h5>
          <p className="text-sm">
            Céréales (blé, maïs, soja), sucre brut en gros volumes, certains produits agricoles. Bulk carriers
            par taille : <Bold>Handysize</Bold> (25-40 kt DWT), <Bold>Handymax/Supramax</Bold> (40-65 kt),
            <Bold> Panamax</Bold> (65-80 kt), Capesize (150 kt+, rare en soft). Frets indexés sur le
            <Bold> Baltic Dry Index (BDI)</Bold> publié quotidiennement par le Baltic Exchange (Londres).
          </p>
          <h5 className="font-serif text-lg text-white mt-4">Conteneurs</h5>
          <p className="text-sm">
            Café, cacao, riz en sacs, coton en balles, huile de palme en citernes, sucre en sacs. Conteneur 20
            pieds : 800 $ en période calme à <Bold>15 000 $+</Bold> pendant la crise COVID 2021.
          </p>
          <h5 className="font-serif text-lg text-white mt-4">Stockage et silos</h5>
          <p className="text-sm">
            Critique pour les soft. Paramètres : <Bold>humidité (le plus important — &lt; 65 % HR)</Bold>,
            température, ventilation, protection insectes / rongeurs. Silos modernes : ventilation forcée et
            contrôle T°C automatisés.
          </p>
        </Sub>

        <Sub title="Modes de paiement">
          <ul className="list-disc list-inside text-sm space-y-2">
            <li><Bold>LC documentaire</Bold> : standard pour gros volumes et premières transactions. Documents : Bill of Lading (ou AWB), Commercial Invoice, Packing List, Certificate of Quality (SGS), Certificate of Origin, Phytosanitary, et certificats spécifiques (Halal, Kosher, bio, Rainforest Alliance)</li>
            <li><Bold>Open Account</Bold> : paiement différé (30-90 j) sur confiance. Uniquement relations établies</li>
            <li><Bold>D/P</Bold> (Documents contre Paiement) : banque acheteur remet documents contre paiement immédiat</li>
            <li><Bold>D/A</Bold> (Documents contre Acceptation) : remise contre acceptation d&apos;une traite à terme</li>
          </ul>
        </Sub>

        <Sub title="Les 4 risques spécifiques aux soft">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <RiskCard
              icon={Globe2}
              title="Climatique — El Niño / La Niña"
              body="El Niño (réchauffement Pacifique) : sécheresses Asie SE, Australie, Inde, Afrique de l&apos;Ouest ; pluies excessives Amérique Sud. La Niña inverse. Effets anticipés 6-12 mois avant récolte."
            />
            <RiskCard
              icon={AlertTriangle}
              title="Mycotoxines"
              body="Aflatoxines (maïs, arachides), DON / Zearalenone (blé), Ochratoxine A (café, cacao). Limites UE strictes. Lot contaminé = refusé en frontière = perte totale."
            />
            <RiskCard
              icon={AlertTriangle}
              title="Restrictions export imprévues"
              body="Inde 2023 (riz), Indonésie 2022 (huile palme), Argentine régulièrement (blé, maïs). Décisions sans préavis qui perturbent les marchés."
            />
            <RiskCard
              icon={AlertTriangle}
              title="Contamination & rejet frontière"
              body="FDA (USA), EFSA (UE) bloquent les lots avec résidus pesticides, OGM non autorisés, organismes nuisibles. Coût rejet (fret retour, stockage, destruction) > valeur du lot."
            />
          </div>
        </Sub>

        <Sub title="Commissions et marges selon les volumes">
          <p>Plus faibles en pourcentage qu&apos;en métaux précieux ou pétrole, mais volumes énormes.</p>
          <div className="border border-white/10 overflow-hidden mt-4">
            <table className="w-full text-sm">
              <thead className="bg-[#141414] text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                <tr>
                  <th className="text-left p-4">Produit</th>
                  <th className="text-left p-4">Taux</th>
                  <th className="text-left p-4">Exemple</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[
                  { p: "Sucre", r: "0,5 – 1,5 %", e: "100 000 t à 600 $/t = 60 M$ → ~ 450 k$ à 0,75 %" },
                  { p: "Café Arabica", r: "1 – 2 %", e: "500 t à 3 500 $/t = 1,75 M$ → ~ 26 250 $" },
                  { p: "Café Specialty", r: "2 – 5 %", e: "Volumes plus faibles, qualification intensive" },
                  { p: "Cacao", r: "0,5 – 1,5 %", e: "10 000 t à 7 000 $/t = 70 M$ → ~ 700 k$ à 1 %" },
                  { p: "Blé", r: "0,3 – 1 %", e: "50 000 t à 220 $/t = 11 M$ → ~ 55 k$ à 0,5 %" },
                  { p: "Maïs", r: "0,3 – 1 %", e: "Volumes très importants — marges fines" },
                  { p: "Riz", r: "0,5 – 1,5 %", e: "20 000 t à 580 $/t = 11,6 M$ → ~ 116 k$ à 1 %" },
                  { p: "Huile de palme", r: "0,5 – 1 %", e: "5 000 t à 1 000 $/t = 5 M$ → ~ 37 500 $ à 0,75 %" },
                ].map((c) => (
                  <tr key={c.p}>
                    <td className="p-4 font-serif text-white">{c.p}</td>
                    <td className="p-4 font-mono text-[#D4AF37]">{c.r}</td>
                    <td className="p-4 text-[#A1A1AA]">{c.e}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Sub>
      </Section>

      {/* ============= CONCLUSION ============= */}
      <Section eyebrow="Conclusion" title="Les 4 clés du succès en soft commodities" index="13">
        <NumberedItem n="01" title="La connaissance technique est irremplaçable">
          Distinguer un ICUMSA 45 d&apos;un VHP, comprendre la différence entre un Yirgacheffe Grade 1 Washed et
          un Brazilian Natural NY 2, expliquer pourquoi un blé 14 % protéines vaut 15 % de plus qu&apos;un blé
          11 % — c&apos;est ce qui vous positionne comme expert.
        </NumberedItem>
        <NumberedItem n="02" title="La veille des marchés est indispensable">
          Les prix bougent quotidiennement : météo, rapports agricoles (USDA <Bold>WASDE</Bold> mensuel = référence
          mondiale), décisions politiques, géopolitique. Un intermédiaire qui ne suit pas les marchés
          quotidiennement ne peut pas conseiller efficacement.
        </NumberedItem>
        <NumberedItem n="03" title="La spécialisation géographique est une force">
          Un intermédiaire spécialisé café est-africain (Éthiopie, Kenya, Rwanda, Burundi) avec un réseau
          solide a une valeur incomparable vs un généraliste. Idem cacao ivoirien, sucre brésilien, riz thaï.
        </NumberedItem>
        <NumberedItem n="04" title="La durabilité — exigence commerciale, pas seulement éthique">
          Nestlé, Unilever, Mars, Mondelez, Ferrero, Starbucks ont tous des engagements publics. En 2030, une
          proportion croissante de café, cacao, sucre et coton commercialisés devra porter des certifications
          durabilité. L&apos;intermédiaire qui connecte producteurs certifiés et acheteurs exigeants crée une
          valeur réelle et durable.
        </NumberedItem>
        <Quote text="Volumes énormes, marges fines, exigences techniques élevées : la spécialisation et la durabilité sont vos deux meilleurs leviers." />
      </Section>
    </div>
  );
}

const RECAP = [
  { p: "Sucre brut", b: "ICE No.11 (NY)", g: "96° pol", pr: "440-507 $/t (20-23 cts/lb)", c: "0,5-1,5 %", i: "FOB / CIF" },
  { p: "Sucre blanc", b: "ICE No.5 (LDN)", g: "ICUMSA 45, 99,8° pol", pr: "550-650 $/t", c: "0,5-1,5 %", i: "CIF / DAP" },
  { p: "Café Arabica", b: "ICE C (NY)", g: "Contrat C", pr: "4 400-6 400 $/t", c: "1-2 %", i: "FOB / CIF" },
  { p: "Café Robusta", b: "ICE Robusta (LDN)", g: "Vietnam Gr2", pr: "2 800-4 200 $/t", c: "1-2 %", i: "FOB" },
  { p: "Cacao", b: "ICE NY + LDN", g: "Grade 1 (CdI / Ghana)", pr: "6 000-9 000 $/t", c: "0,5-1,5 %", i: "FOB / CIF" },
  { p: "Blé", b: "CBOT + MATIF", g: "US N°2 HRW / FR PS76", pr: "165-220 $/t", c: "0,3-1 %", i: "FOB / CIF" },
  { p: "Maïs", b: "CBOT", g: "US Yellow N°2", pr: "165-189 $/t", c: "0,3-1 %", i: "FOB / CIF" },
  { p: "Riz blanc 5%", b: "OTC (FOB Bangkok)", g: "Thai 5%", pr: "500-650 $/t", c: "0,5-1,5 %", i: "FOB / CIF" },
  { p: "Huile palme CPO", b: "BMD", g: "FFA max 5 %", pr: "900-1 050 $/t", c: "0,5-1 %", i: "FOB / CIF" },
  { p: "Coton", b: "ICE Cotton No.2", g: "Upland US", pr: "1 650-1 870 $/t", c: "0,5-1,5 %", i: "CFR / CIF" },
];

// ---------- Local subcomponents ----------
const TrendingUpIcon = (props) => <Award {...props} />;

const GradeCard = ({ name, purity, body, accent }) => (
  <div className={`border ${accent ? "border-[#D4AF37]/40" : "border-white/10"} bg-[#0c0c0c] p-5 hover:border-[#D4AF37]/60 transition-colors`}>
    <div className="flex items-center gap-3">
      <Coffee className="h-4 w-4 text-[#D4AF37]" />
      <div className="font-serif text-base text-white flex-1">{name}</div>
    </div>
    <div className="font-mono text-xs text-[#D4AF37] mt-2">{purity}</div>
    <p className="mt-3 text-xs text-[#A1A1AA] leading-relaxed">{body}</p>
  </div>
);

const ProducersBar = ({ label, items }) => (
  <div className="mt-6">
    <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">{label}</div>
    <div className="flex flex-wrap gap-2">
      {items.map((i) => (
        <div key={i.country} className="flex items-center gap-2 px-3 py-2 border border-white/10 bg-[#0c0c0c]">
          <span className="text-sm text-white">{i.country}</span>
          <span className="font-mono text-xs text-[#D4AF37]">{i.pct}</span>
        </div>
      ))}
    </div>
  </div>
);

const PriceTable = ({ rows, record }) => (
  <div className="mt-4 border border-white/10 overflow-hidden">
    <table className="w-full text-sm">
      <tbody className="divide-y divide-white/5">
        {rows.map((r) => (
          <tr key={r.y}>
            <td className="p-3 font-mono text-[#D4AF37] w-44 align-top">{r.y}</td>
            <td className="p-3 text-[#C4C4C7]">{r.v}</td>
          </tr>
        ))}
        {record && (
          <tr className="bg-[#141414]">
            <td className="p-3 font-mono text-[#FF3B30] w-44">{"\u2605"}</td>
            <td className="p-3 text-[#F5F5DC] italic">{record}</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const RegionalPrimes = ({ rows }) => (
  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
    {rows.map((r) => (
      <div key={r.region} className="flex items-center justify-between border border-white/10 bg-[#0c0c0c] p-4">
        <span className="text-sm text-[#C4C4C7]">{r.region}</span>
        <span className="font-mono text-sm text-[#D4AF37]">{r.value}</span>
      </div>
    ))}
  </div>
);

const RiskCard = ({ icon: Icon, title, body }) => (
  <div className="border border-[#FF3B30]/20 bg-[#FF3B30]/5 p-5">
    <div className="flex items-center gap-3">
      <Icon className="h-5 w-5 text-[#FF3B30]" />
      <div className="font-serif text-base text-white">{title}</div>
    </div>
    <p className="mt-3 text-xs text-[#C4C4C7] leading-relaxed">{body}</p>
  </div>
);
